// Android multi-lens camera collapse.
//
// Chrome's Camera2 enumeration on a multi-lens Android phone reports one entry
// per physical lens. The reported device offered four — "camera2 1, facing
// front", "camera2 3, facing front", "camera2 2, facing back", "camera2 0,
// facing back" — where its owner expects two, and only the two Android reserves
// as primary actually captured. Desktop webcams and iOS Safari's genuinely
// distinct lenses must pass through untouched, so the collapse is gated on the
// Android label shape AND on two or more entries sharing a facing (AT-14).

// Chrome's Android label, e.g. "camera2 0, facing back". Not localized.
const ANDROID_LABEL = /^camera2?\s*(\d+)/i

function androidIndex(device) {
  const match = ANDROID_LABEL.exec(device.label || "")
  return match ? Number(match[1]) : null
}

// InputDeviceInfo.getCapabilities() carries facingMode on Chromium once camera
// permission is held — which the caller already holds by the time it enumerates.
// The label is the fallback for browsers that do not implement it.
function facingOf(device) {
  const capabilities = device.getCapabilities?.()
  const facingMode = Array.isArray(capabilities?.facingMode) ? capabilities.facingMode[0] : capabilities?.facingMode
  if (facingMode === "user") return "front"
  if (facingMode === "environment") return "back"
  if (facingMode) return String(facingMode)

  const match = /facing\s+(front|back)/i.exec(device.label || "")
  return match ? match[1].toLowerCase() : null
}

export default function collapseAndroidCameras(cameras) {
  const byFacing = new Map()
  cameras.forEach((camera) => {
    const facing = facingOf(camera)
    const index = androidIndex(camera)
    if (!facing || index === null) return
    byFacing.set(facing, [...(byFacing.get(facing) || []), { camera, index }])
  })

  const dropped = new Set()
  byFacing.forEach((group) => {
    if (group.length < 2) return
    // Keep the lowest camera id in the group, not the first enumerated. Android
    // reserves 0 for the primary back lens and 1 for the primary front — the two
    // confirmed working — and enumeration order is not that order (the reported
    // device enumerated 1, 3, 2, 0).
    const keep = group.reduce((best, entry) => (entry.index < best.index ? entry : best))
    group.forEach((entry) => {
      if (entry !== keep) dropped.add(entry.camera.deviceId)
    })
  })

  return dropped.size === 0 ? cameras : cameras.filter((camera) => !dropped.has(camera.deviceId))
}
